import { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Gift } from 'lucide-react';
import { subscription } from '@/lib/api';

// Redeems a Trial Premium coupon (KAN-136): 30 days of full access, no credit card. Shown to
// Head Start accounts that haven't used their one trial; the server enforces every eligibility rule.
export default function TrialCouponRedeem({ onRedeemed }) {
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!code.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await subscription.redeemTrialCoupon(code.trim());
      toast.success('Trial Premium activated — enjoy 30 days of full access!');
      setCode('');
      if (onRedeemed) await onRedeemed();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not redeem this code');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="border-slate-200 mb-8" data-testid="trial-coupon-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-1">
          <Gift className="h-5 w-5 text-indigo-600" />
          <p className="font-medium text-slate-900">Have a Trial Premium code?</p>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Redeem it for 30 days of full access to every feature — no credit card needed.
        </p>
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="TRIAL-XXXX-XXXX"
            aria-label="Trial Premium code"
            className="sm:max-w-xs uppercase"
            data-testid="trial-coupon-input"
          />
          <Button
            type="submit"
            disabled={isSubmitting || !code.trim()}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="trial-coupon-submit-btn"
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Redeem code
          </Button>
        </form>
        {error && (
          <p className="text-sm text-rose-600 mt-3" data-testid="trial-coupon-error">{error}</p>
        )}
      </CardContent>
    </Card>
  );
}
